import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAppBase } from "@/hooks/useAppBase";
import { useApiClient } from "@/lib/apiClient";
import { getAgencyOperationalBadge, getAiBadge } from "@/lib/agencyBadges";
import { toast } from "sonner";
import {
  Users, Phone, Calendar, Plus, Settings2, X, GripVertical, Trash2, Save,
  Search, Filter, UserPlus, StickyNote, MessageSquare, Pencil, Check, User as UserIcon, Target,
} from "lucide-react";
import SortPopover from "@/components/SortPopover";
import { useTranslation, Trans } from "react-i18next";
import { fmtDateTime } from "@/lib/format";
import QuickAppointmentModal from "@/components/QuickAppointmentModal";

// Palette 10 colori — allineata a DEFAULT_PALETTE (Tailwind 400) di ColorSwatchGrid
const STAGE_PALETTE = [
  "#60A5FA", "#22D3EE", "#2DD4BF", "#4ADE80", "#FBBF24",
  "#FB923C", "#F87171", "#F472B6", "#A78BFA", "#94A3B8",
];

// Sort options for the pipeline SortPopover (compact GHL-style)
const PIPELINE_SORT_FIELDS = [
  { key: "created_at", label: "pipeline.sort.created_at" },
  { key: "pipeline_updated_at", label: "pipeline.sort.pipeline_updated_at" },
  { key: "name", label: "pipeline.sort.name" },
  { key: "phone", label: "pipeline.sort.phone" },
  { key: "source", label: "pipeline.sort.source" },
];

/**
 * Pipeline stile GoHighLevel:
 * - Scroll orizzontale interno al board (non push out del layout).
 * - Scroll verticale per-colonna, no scroll di pagina.
 * - Ricerca + filtri + ordinamento tramite drawer laterale.
 * - "Nuova opportunità": link cliente esistente o crea nuovo, in prima fase.
 */
export default function Pipeline({ agency = false, agencyCases = {}, onOpenClient = null, reloadSignal = 0, tenantName = "", onOpenAgenda = null }) {
  const { t } = useTranslation();
  const base = useAppBase();
  const api = useApiClient();
  const [board, setBoard] = useState({ stages: [], columns: {}, counts: {} });
  const [notesCounts, setNotesCounts] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const isAdmin = base === "/admin" && !agency;
  const [dragging, setDragging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showNewOpp, setShowNewOpp] = useState(false);
  const [apptClient, setApptClient] = useState(null); // { id } — opens QuickAppointmentModal inline
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState({
    tags: [], source: "", created_since: "", created_until: "",
    updated_since: "", updated_until: "",
  });
  const boardRef = useRef(null);
  const autoScrollTimer = useRef(null);
  const loadSeq = useRef(0);
  const nav = useNavigate();

  // Mappa unread per contatto (dai thread WhatsApp) — aggiornata in polling.
  const [unreadByClient, setUnreadByClient] = useState({});
  const loadUnread = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/threads");
      const map = {};
      (r.data || []).forEach((t) => { if (t.client_id && t.unread) map[t.client_id] = true; });
      setUnreadByClient(map);
    } catch { /* ignore */ }
  }, [api]);
  useEffect(() => { loadUnread(); const id = setInterval(loadUnread, 20000); return () => clearInterval(id); }, [loadUnread, reloadSignal]);

  const openChat = useCallback((c) => {
    nav(agency ? `/admin/agency/lead-inbox?client=${c.id}` : `/app/inbox?client=${c.id}`);
  }, [agency, nav]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const [rBoard, rCounts] = await Promise.all([
        api.get("/pipeline/board"),
        api.get("/clients/notes-counts").catch(() => ({ data: { counts: {} } })),
      ]);
      if (seq !== loadSeq.current) return; // risposta stale (tenant cambiato) → ignora
      setBoard(rBoard.data);
      setNotesCounts(rCounts.data?.counts || {});
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  // Ricarico la board quando un'azione esterna (es. cambio stage dal Contact
  // Drawer) lo richiede, senza refresh completo della pagina.
  useEffect(() => { if (reloadSignal) load(); }, [reloadSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Team members del workspace HQ — usati per il picker "Assegna" sulle card
  // (solo in modalità admin: sul tenant non c'è mai un admin panel).
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/tenant/members").then((r) => {
      setTeamMembers([...(r.data.members || [])]);
    }).catch(() => {});
  }, [isAdmin]);

  const moveCard = async (clientId, toStage) => {
    try {
      await api.patch(`/pipeline/${clientId}/stage`, { stage: toStage });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };

  const onDragStart = (card, fromStage) => setDragging({ card, fromStage });
  const onDragOver = (e) => e.preventDefault();

  // Called from PipelineCard when a note is added/deleted via the popover.
  const bumpNotesCount = (clientId, delta) => {
    setNotesCounts((prev) => ({
      ...prev,
      [clientId]: Math.max(0, (prev[clientId] || 0) + delta),
    }));
  };

  // Auto-scroll horizontal board while dragging near left/right edges.
  // Uses window drag events so it works over both cards and empty column areas.
  useEffect(() => {
    if (!dragging) {
      if (autoScrollTimer.current) { clearInterval(autoScrollTimer.current); autoScrollTimer.current = null; }
      return;
    }
    const EDGE = 90;      // px from edge that triggers scroll
    const MAX_SPEED = 22; // px per tick at max intensity
    let direction = 0;    // -1 left, +1 right
    let speed = 0;

    const handleDragOver = (e) => {
      const el = boardRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX;
      // Ignore drags outside the board bounds
      if (x < rect.left || x > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        direction = 0; speed = 0;
        return;
      }
      const leftDist = x - rect.left;
      const rightDist = rect.right - x;
      if (leftDist < EDGE) {
        direction = -1;
        speed = Math.round(((EDGE - leftDist) / EDGE) * MAX_SPEED);
      } else if (rightDist < EDGE) {
        direction = 1;
        speed = Math.round(((EDGE - rightDist) / EDGE) * MAX_SPEED);
      } else {
        direction = 0; speed = 0;
      }
    };
    window.addEventListener("dragover", handleDragOver);
    autoScrollTimer.current = setInterval(() => {
      const el = boardRef.current;
      if (!el || direction === 0 || speed === 0) return;
      el.scrollBy({ left: direction * speed, behavior: "auto" });
    }, 16);
    return () => {
      window.removeEventListener("dragover", handleDragOver);
      if (autoScrollTimer.current) { clearInterval(autoScrollTimer.current); autoScrollTimer.current = null; }
    };
  }, [dragging]);

  const onDrop = (toStage) => {
    if (dragging && dragging.fromStage !== toStage) {
      moveCard(dragging.card.id, toStage);
    }
    setDragging(null);
  };
  const bookAppointment = (client) => setApptClient(client);

  const filterAndSortCards = useCallback((cards) => {
    const q = search.trim().toLowerCase();
    let out = cards.filter((c) => {
      if (filters.source && c.source !== filters.source) return false;
      if (filters.tags.length && !filters.tags.every((t) => (c.tags || []).includes(t))) return false;
      if (filters.created_since && (c.created_at || "") < filters.created_since) return false;
      if (filters.created_until && (c.created_at || "") > `${filters.created_until}T23:59:59.999Z`) return false;
      if (filters.updated_since && (c.pipeline_updated_at || "") < filters.updated_since) return false;
      if (filters.updated_until && (c.pipeline_updated_at || "") > `${filters.updated_until}T23:59:59.999Z`) return false;
      if (q) {
        const hay = `${c.name || ""} ${c.phone || ""} ${c.email || ""} ${(c.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "it", { numeric: true }) * dir;
    });
    return out;
  }, [search, sortField, sortDir, filters]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.source) n++;
    if (filters.tags.length) n++;
    if (filters.created_since || filters.created_until) n++;
    if (filters.updated_since || filters.updated_until) n++;
    return n;
  }, [filters]);

  return (
    <div data-testid="pipeline-page" className="space-y-3 flex flex-col" style={{ height: "calc(100vh - 88px)" }}>
      <header className="flex items-center justify-between flex-wrap gap-2 flex-shrink-0">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-black tracking-tight inline-flex items-center gap-2">
            <Users className="h-6 w-6 text-[color:var(--sp-brand)]" />
            <span data-testid="pipeline-title">{t("pipeline.title")}{agency && tenantName ? <span className="text-[color:var(--sp-brand)]"> · {tenantName}</span> : null}</span>
          </h1>
          {!agency && (
            <p className="text-[color:var(--sp-text-secondary)] mt-1 text-xs max-w-2xl">
              {t("pipeline.subtitle")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {agency && onOpenAgenda && (
            <button data-testid="pipeline-open-agenda-btn" onClick={onOpenAgenda}
              className="sp-btn-primary inline-flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4" /> {t("pipeline.salonAgenda")}
            </button>
          )}
          {!agency && (
            <>
              <button data-testid="pipeline-settings-btn" onClick={() => setShowSettings(true)}
                className="sp-btn-ghost inline-flex items-center gap-2 text-sm">
                <Settings2 className="h-4 w-4" /> {t("pipeline.customize")}
              </button>
              <button data-testid="pipeline-new-opp-btn" onClick={() => setShowNewOpp(true)}
                className="sp-btn-primary inline-flex items-center gap-2">
                <Plus className="h-4 w-4" /> {t("pipeline.newOpportunity")}
              </button>
            </>
          )}
        </div>
      </header>

      {/* Search + Sort + Filter bar (stile Contatti) */}
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center flex-shrink-0">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("pipeline.searchPlaceholder")} data-testid="pipeline-search-input"
            className="sp-input !pl-10 !py-1.5 text-sm" />
        </div>
        <SortPopover
          fields={PIPELINE_SORT_FIELDS}
          field={sortField} dir={sortDir}
          onChange={(f, d) => { setSortField(f); setSortDir(d); }}
          testid="pipeline-sort"
          defaultField="created_at" defaultDir="desc" />
        <button data-testid="pipeline-filter-btn" onClick={() => setShowFilters(true)}
          className="sp-btn-ghost inline-flex items-center gap-2 text-sm relative">
          <Filter className="h-4 w-4" /> {t("pipeline.filters")}
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[color:var(--sp-brand)] text-white text-[9px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[color:var(--sp-text-muted)]">{t("common.loading")}</div>
      ) : (
        // Overflow container: scroll orizzontale SOLO qui, non nella pagina
        <div ref={boardRef} className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden rounded-2xl"
             data-testid="pipeline-board">
          <div className="inline-flex gap-3 h-full pb-2">
            {board.stages.map((stage) => {
              const rawItems = board.columns[stage.key] || [];
              const items = filterAndSortCards(rawItems);
              const active = dragging && dragging.fromStage !== stage.key;
              return (
                <div
                  key={stage.key}
                  data-testid={`col-${stage.key}`}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(stage.key)}
                  className={`w-[300px] flex-shrink-0 rounded-2xl bg-[color:var(--sp-subtle)]/40 border transition-colors flex flex-col min-h-0 ${active ? "border-[color:var(--sp-brand)] border-dashed bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)]"}`}
                >
                  <div className="p-3 border-b border-[color:var(--sp-border)] flex items-center gap-2 bg-white/80 backdrop-blur rounded-t-2xl">
                    <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: stage.color }} />
                    <span className="text-sm font-bold text-[color:var(--sp-text)] truncate">{stage.label}</span>
                    <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-white text-[color:var(--sp-text-secondary)] border border-[color:var(--sp-border)]"
                          data-testid={`col-${stage.key}-count`}>
                      {items.length}{rawItems.length !== items.length ? `/${rawItems.length}` : ""}
                    </span>
                  </div>
                  <div className="p-2 space-y-2 overflow-y-auto flex-1 min-h-0">
                    {items.length === 0 ? (
                      <div className="p-6 text-[11px] text-[color:var(--sp-text-muted)] text-center italic">
                        {rawItems.length === 0 ? t("pipeline.dragHere") : t("pipeline.noResults")}
                      </div>
                    ) : items.map((c) => (
                      <PipelineCard key={c.id} c={c} stageColor={stage.color}
                        notesCount={notesCounts[c.id] || 0}
                        onNotesChanged={(delta) => bumpNotesCount(c.id, delta)}
                        onDragStart={() => onDragStart(c, stage.key)}
                        onBook={() => bookAppointment(c)}
                        teamMembers={teamMembers}
                        isAdmin={isAdmin}
                        agency={agency}
                        agencyCase={agencyCases[c.id]}
                        onOpenClient={onOpenClient}
                        onOpenChat={openChat}
                        unread={!!unreadByClient[c.id]}
                        onAssignedChange={load} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showSettings && (
        <StagesSettingsModal stages={board.stages}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); load(); }} />
      )}
      {showFilters && (
        <FiltersDrawer filters={filters} setFilters={setFilters}
          onClose={() => setShowFilters(false)} />
      )}
      {showNewOpp && (
        <NewOpportunityModal stages={board.stages}
          onClose={() => setShowNewOpp(false)}
          onCreated={() => { setShowNewOpp(false); load(); }} />
      )}
      {apptClient && (
        <QuickAppointmentModal
          clientId={apptClient.id}
          onClose={() => setApptClient(null)}
          onSaved={() => { setApptClient(null); load(); }}
        />
      )}
    </div>
  );
}

function PipelineCard({ c, stageColor, notesCount = 0, onNotesChanged, onDragStart, onBook, teamMembers = [], isAdmin = false, agency = false, agencyCase = null, onOpenClient = null, onOpenChat = null, unread = false, onAssignedChange }) {
  const { t } = useTranslation();
  const base = useAppBase();
  const api = useApiClient();
  const appt = c.next_appointment;
  const hasNotes = notesCount > 0;
  const [pinned, setPinned] = useState(false);        // click = stays open
  const [previewNotes, setPreviewNotes] = useState(null); // null=not fetched, []=empty, [...]=loaded
  const [popPos, setPopPos] = useState(null); // { top, left } in viewport coords
  const [noteDraft, setNoteDraft] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const anchorRef = useRef(null);  // the notes button
  const popRef = useRef(null);      // the popover itself (in portal)

  const isOpen = pinned;

  // Compute popover position relative to viewport, keeping it inside the window.
  const computePos = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POP_W = 480;
    const POP_MAX_H = 520;
    const GAP = 6;
    // Center-align popover on the anchor's horizontal center
    const anchorCenter = r.left + r.width / 2;
    let left = anchorCenter - POP_W / 2;
    if (left < 12) left = 12;
    if (left + POP_W > window.innerWidth - 12) left = window.innerWidth - POP_W - 12;
    let top = r.bottom + GAP;
    // If not enough room below, open above
    if (top + POP_MAX_H > window.innerHeight - 12) {
      const above = r.top - POP_MAX_H - GAP;
      if (above >= 12) top = above;
      else top = Math.max(12, window.innerHeight - POP_MAX_H - 12);
    }
    setPopPos({ top, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    computePos();
    const onResize = () => computePos();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [isOpen]);

  const fetchNotes = async (limit = 3) => {
    try {
      const r = await api.get(`/clients/${c.id}/activities?type=manual&limit=${limit}`);
      setPreviewNotes(r.data.items || []);
    } catch { setPreviewNotes([]); }
  };

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text || addingNote) return;
    setAddingNote(true);
    try {
      await api.post(`/clients/${c.id}/activities/manual`, { text });
      setNoteDraft("");
      await fetchNotes(20);
      onNotesChanged?.(+1);
      toast.success(t("pipeline.noteAdded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pipeline.noteError"));
    }
    setAddingNote(false);
  };

  const deleteNote = async (noteId) => {
    if (!confirm(t("pipeline.deleteNoteConfirm"))) return;
    try {
      await api.delete(`/clients/${c.id}/activities/${noteId}`);
      setPreviewNotes((prev) => (prev || []).filter((n) => n.id !== noteId));
      onNotesChanged?.(-1);
    } catch (e) {
      toast.error(t("pipeline.deleteError"));
    }
  };

  const togglePin = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pinned) { setPinned(false); return; }
    setPinned(true);
    if (previewNotes === null) fetchNotes(20);  // load full list on open
  };

  // Click outside closes pinned popover
  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (e) => {
      const inAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inAnchor && !inPop) {
        setPinned(false);
      }
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDocClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDocClick); };
  }, [pinned]);

  return (
    <div draggable onDragStart={onDragStart}
      data-testid={`card-${c.id}`}
      className="p-3 rounded-xl bg-white border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]/60 hover:shadow-md cursor-move transition-all relative">
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: stageColor }} />
        {onOpenClient ? (
          <button type="button" onClick={(e) => { e.stopPropagation(); onOpenClient(c); }}
            data-testid={`card-${c.id}-open`}
            className="font-bold text-sm truncate hover:text-[color:var(--sp-brand)] flex-1 text-left">
            {c.name}
          </button>
        ) : (
          <Link to={`${base}/clients/${c.id}`} className="font-bold text-sm truncate hover:text-[color:var(--sp-brand)] flex-1">
            {c.name}
          </Link>
        )}
        {onOpenChat && (
          <button type="button" onMouseDown={(e) => e.stopPropagation()} draggable={false}
            onClick={(e) => { e.stopPropagation(); onOpenChat(c); }}
            data-testid={`card-${c.id}-chat-btn`} title={t("pipeline.openChat")}
            className="relative h-6 w-6 rounded-md flex items-center justify-center text-[color:var(--sp-text-muted)] hover:bg-emerald-50 hover:text-emerald-600 transition-colors flex-shrink-0">
            <MessageSquare className="h-3.5 w-3.5" />
            {unread && <span data-testid={`card-${c.id}-chat-unread`} className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#25D366] ring-2 ring-white" />}
          </button>
        )}
        <div className="relative">
          <button
            ref={anchorRef}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={togglePin}
            draggable={false}
            data-testid={`card-${c.id}-notes-btn`}
            aria-expanded={isOpen}
            title={hasNotes ? t("pipeline.notesCount", { n: notesCount }) : t("pipeline.addNote")}
            className={`relative h-6 w-6 rounded-md flex items-center justify-center transition-colors ${
              hasNotes
                ? "text-[#3B82F6] hover:bg-blue-50"
                : "text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)] hover:text-[#3B82F6]"
            } ${pinned ? "bg-blue-50" : ""}`}>
            <StickyNote className="h-3.5 w-3.5" />
            {hasNotes && (
              <span
                data-testid={`card-${c.id}-notes-badge`}
                className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#3B82F6] text-white text-[9px] font-bold flex items-center justify-center leading-none ring-2 ring-white">
                {notesCount > 9 ? "9+" : notesCount}
              </span>
            )}
          </button>
          {isOpen && popPos && createPortal(
            <div
              ref={popRef}
              data-testid={`card-${c.id}-notes-preview`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{ position: "fixed", top: popPos.top, left: popPos.left, width: 480 }}
              className="z-[9999] rounded-2xl border border-[color:var(--sp-border)] bg-white shadow-2xl cursor-default overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--sp-border)]/60 bg-[color:var(--sp-subtle)]/40">
                <div className="flex items-center gap-2 text-[12px] font-bold text-[color:var(--sp-text)] uppercase tracking-wider">
                  <StickyNote className="h-4 w-4 text-[#3B82F6]" />
                  {c.name} — {t("pipeline.notesTitle", { n: notesCount })}
                </div>
                {pinned && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPinned(false); }}
                    data-testid={`card-${c.id}-notes-preview-close`}
                    className="h-6 w-6 rounded flex items-center justify-center hover:bg-white text-[color:var(--sp-text-muted)]"
                    title={t("common.close")}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Add-note row — always available so notes can be added from the pipeline
                  without opening the client detail (parity with Anagrafica timeline). */}
              <div className="px-4 py-2.5 border-b border-[color:var(--sp-border)]/60 bg-white">
                <div className="flex gap-2 items-end">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addNote(); }
                    }}
                    placeholder={t("pipeline.addNotePlaceholder")}
                    rows={2}
                    data-testid={`card-${c.id}-notes-add-input`}
                    className="sp-input flex-1 text-[13px] resize-none py-1.5"
                  />
                  <button
                    type="button"
                    onClick={addNote}
                    disabled={!noteDraft.trim() || addingNote}
                    data-testid={`card-${c.id}-notes-add-btn`}
                    title={t("pipeline.addNote")}
                    className="h-8 w-8 rounded-md border border-[color:var(--sp-border)] bg-white hover:bg-[color:var(--sp-brand)]/10 hover:border-[color:var(--sp-brand)] hover:text-[color:var(--sp-brand)] text-[color:var(--sp-text-secondary)] flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:hover:bg-white disabled:hover:border-[color:var(--sp-border)] disabled:hover:text-[color:var(--sp-text-secondary)] transition-colors">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="max-h-[380px] overflow-y-auto p-3 space-y-2">
                {previewNotes === null ? (
                  <div className="text-[12px] text-[color:var(--sp-text-muted)] py-3 text-center">{t("common.loading")}</div>
                ) : previewNotes.length === 0 ? (
                  <div className="text-[12px] text-[color:var(--sp-text-muted)] py-4 text-center italic">
                    {t("pipeline.noNotes")}
                  </div>
                ) : (
                  previewNotes.map((n) => (
                    <div key={n.id} data-testid={`card-${c.id}-note-${n.id}`}
                         className="group rounded-lg bg-[color:var(--sp-subtle)]/50 p-3 border border-[color:var(--sp-border)]/50 hover:border-[color:var(--sp-border)]">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-[13px] text-[color:var(--sp-text)] whitespace-pre-wrap break-words leading-relaxed flex-1">
                          {n.description || "—"}
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteNote(n.id)}
                          data-testid={`card-${c.id}-note-del-${n.id}`}
                          title={t("pipeline.deleteNote")}
                          className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded flex items-center justify-center text-red-600 hover:bg-red-50 flex-shrink-0 transition-opacity">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-2">
                        {fmtDateTime(n.created_at, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {n.actor_name || t("pipeline.system")}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="px-4 py-2.5 border-t border-[color:var(--sp-border)]/60 bg-white flex items-center justify-between">
                <span className="text-[11px] text-[color:var(--sp-text-muted)]">
                  {t("pipeline.clickOutside")}
                </span>
                {!agency && (
                  <Link
                    to={`/app/clients/${c.id}?tab=activity`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`card-${c.id}-notes-open-link`}
                    className="text-[12px] font-bold text-[#3B82F6] hover:underline">
                    {t("pipeline.openCard")}
                  </Link>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
      {c.phone && <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</div>}
      {/* Agency Case overlay — badge operativo/AI (view composta, nessuna copia dati) */}
      {agency && agencyCase && (() => {
        const op = getAgencyOperationalBadge(agencyCase);
        const ai = getAiBadge(agencyCase);
        if (!op && !ai) return null;
        return (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`card-${c.id}-agency-badges`}>
            {op && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold ${op.cls}`}><op.Icon className="h-3 w-3" />{op.label}</span>}
            {ai && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold ${ai.cls}`}><ai.Icon className="h-3 w-3" />{ai.label}</span>}
          </div>
        );
      })()}
      {/* Assign + commerce_status controls — solo in modalità admin (HQ) */}
      {isAdmin && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <AssignChip client={c} members={teamMembers} onChanged={onAssignedChange} />
          <CommerceStatusChip client={c} onChanged={onAssignedChange} />
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1 items-center">
        {c.acquisition_source === "meta_ads" && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] uppercase tracking-wider"
                data-testid={`card-${c.id}-ads`}>
            {t("pipeline.leadMetaAds")}
          </span>
        )}
        {c.tags && c.tags.filter((t) => t !== "lead_ads").slice(0, 2).map((t) => (
          <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] font-bold">{t}</span>
        ))}
        {c.tags && c.tags.filter((t) => t !== "lead_ads").length > 2 && (
          <span className="text-[10px] text-[color:var(--sp-text-muted)]">+{c.tags.filter((t) => t !== "lead_ads").length - 2}</span>
        )}
      </div>
      {appt ? (
        <div className="mt-2 rounded-lg bg-blue-50 border border-blue-200 px-2 py-1.5"
             data-testid={`card-${c.id}-appt`}>
          <div className="flex items-center gap-1.5 text-[11px] text-blue-800 font-bold">
            <Calendar className="h-3 w-3" />
            {formatApptDate(appt.start)}          </div>
          {appt.notes && <div className="text-[10px] text-blue-700 mt-0.5 truncate">{appt.notes}</div>}
        </div>
      ) : (
        <button data-testid={`card-${c.id}-book-btn`}
          onClick={(e) => { e.stopPropagation(); onBook(); }}
          className="mt-2 w-full text-[11px] font-bold text-[color:var(--sp-brand)] hover:bg-[color:var(--sp-brand)]/5 py-1.5 rounded-lg border border-dashed border-[color:var(--sp-brand)]/40 inline-flex items-center justify-center gap-1">
          <Plus className="h-3 w-3" /> {t("pipeline.bookAppointment")}
        </button>
      )}
    </div>
  );
}

function formatApptDate(iso) {
  if (!iso) return "";
  return fmtDateTime(iso, {
    weekday: "short", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Assignee chip — click apre menu team members, salva su PATCH /clients/{id}
// ---------------------------------------------------------------------------
const AVATAR_COLORS = ["#3B82F6", "#06B6D4", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444"];
const _initials = (n) => (n || "?").split(" ").slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("");
const _colorFor = (id) => {
  let h = 0; for (const ch of (id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

function AssignChip({ client, members, onChanged }) {
  const { t } = useTranslation();
  const api = useApiClient();
  const [open, setOpen] = useState(false);
  const current = members.find((m) => m.id === client.assigned_to);
  const assign = async (uid) => {
    try {
      await api.patch(`/clients/${client.id}`, { assigned_to: uid });
      toast.success(uid ? t("pipeline.assigned") : t("pipeline.assignRemoved"));
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={`card-${client.id}-assign-btn`}
        title={current ? t("pipeline.assignedTo", { name: current.name || current.email }) : t("pipeline.assignTo")}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] text-[10px] font-bold">
        {current ? (
          <span className="w-4 h-4 rounded-full text-white font-bold text-[9px] flex items-center justify-center" style={{ background: _colorFor(current.id) }}>
            {_initials(current.name || current.email)}
          </span>
        ) : <UserIcon className="h-3 w-3 text-[color:var(--sp-text-muted)]" />}
        <span className="truncate max-w-[80px]">{current ? (current.name || current.email) : t("pipeline.assign")}</span>
      </button>
      {open && (
        <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
          data-testid={`card-${client.id}-assign-menu`}
          className="absolute z-30 top-full mt-1 left-0 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-lg min-w-[200px] max-h-56 overflow-y-auto">
          <button type="button" onClick={() => assign(null)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--sp-subtle)] border-b border-[color:var(--sp-border)]">
            <span className="text-[color:var(--sp-text-muted)]">{t("pipeline.noOne")}</span>
          </button>
          {members.map((m) => (
            <button key={m.id} type="button" onClick={() => assign(m.id)}
              data-testid={`card-${client.id}-assign-${m.id}`}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[color:var(--sp-subtle)] ${client.assigned_to === m.id ? "bg-[color:var(--sp-brand)]/5" : ""}`}>
              <span className="w-5 h-5 rounded-full text-white font-bold text-[9px] flex items-center justify-center" style={{ background: _colorFor(m.id) }}>
                {_initials(m.name || m.email)}
              </span>
              <span className="text-xs flex-1 truncate">{m.name || m.email}</span>
              {client.assigned_to === m.id && <Check className="h-3.5 w-3.5 text-[color:var(--sp-brand)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const COMMERCE_STATUSES = [
  { key: "buyer_rapport",        color: "#10B981" },
  { key: "buyer_no_rapport",     color: "#3B82F6" },
  { key: "non_buyer_rapport",    color: "#F59E0B" },
  { key: "non_buyer_no_rapport", color: "#F87171" },
  { key: "never_heard",          color: "#94A3B8" },
];

function CommerceStatusChip({ client, onChanged }) {
  const { t } = useTranslation();
  const api = useApiClient();
  const [open, setOpen] = useState(false);
  const current = COMMERCE_STATUSES.find((s) => s.key === client.commerce_status);
  const setStatus = async (key) => {
    try {
      await api.patch(`/clients/${client.id}`, { commerce_status: key });
      toast.success(t("pipeline.statusUpdated"));
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={`card-${client.id}-status-btn`}
        title={t("pipeline.commerceStatus")}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-bold"
        style={{ borderColor: current?.color || "var(--sp-border)", color: current?.color || "#94A3B8" }}>
        <Target className="h-3 w-3" />
        <span className="truncate max-w-[100px]">{current ? t(`pipeline.statuses.${current.key}`) : t("pipeline.statusShort")}</span>
      </button>
      {open && (
        <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
          data-testid={`card-${client.id}-status-menu`}
          className="absolute z-30 top-full mt-1 left-0 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-lg min-w-[220px]">
          <button type="button" onClick={() => setStatus(null)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--sp-subtle)] border-b border-[color:var(--sp-border)] text-[color:var(--sp-text-muted)]">
            {t("pipeline.noStatus")}
          </button>
          {COMMERCE_STATUSES.map((s) => (
            <button key={s.key} type="button" onClick={() => setStatus(s.key)}
              data-testid={`card-${client.id}-status-${s.key}`}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[color:var(--sp-subtle)] ${client.commerce_status === s.key ? "bg-[color:var(--sp-brand)]/5" : ""}`}>
              <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <span className="text-xs flex-1">{t(`pipeline.statuses.${s.key}`)}</span>
              {client.commerce_status === s.key && <Check className="h-3.5 w-3.5 text-[color:var(--sp-brand)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FiltersDrawer({ filters, setFilters, onClose }) {
  const { t } = useTranslation();
  const api = useApiClient();
  const [tags, setTags] = useState([]);
  const [f, setF] = useState(filters);
  useEffect(() => {
    api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
  }, []);
  const toggleTag = (name) => {
    setF((p) => ({ ...p, tags: p.tags.includes(name) ? p.tags.filter((t) => t !== name) : [...p.tags, name] }));
  };
  const apply = () => { setFilters(f); onClose(); };
  const reset = () => {
    const empty = { tags: [], source: "", created_since: "", created_until: "",
      updated_since: "", updated_until: "" };
    setF(empty); setFilters(empty);
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}
         data-testid="pipeline-filters-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-black">{t("pipeline.filters")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("pipeline.drawer.tags")}</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tags.length === 0 && <div className="text-xs text-[color:var(--sp-text-muted)]">{t("pipeline.drawer.noTags")}</div>}
              {tags.map((t) => {
                const on = f.tags.includes(t.name);
                return (
                  <button key={t.id} onClick={() => toggleTag(t.name)}
                    data-testid={`filter-tag-${t.name}`}
                    className="px-2 py-1 rounded-full text-[10px] font-bold border"
                    style={on
                      ? { background: t.color, borderColor: t.color, color: "#fff" }
                      : { borderColor: `${t.color}55`, background: `${t.color}10`, color: t.color }}>
                    {on ? "✓ " : ""}#{t.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("pipeline.drawer.createdBetween")}</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input type="date" value={f.created_since} onChange={(e) => setF({ ...f, created_since: e.target.value })}
                className="sp-input" data-testid="filter-created-since" />
              <input type="date" value={f.created_until} onChange={(e) => setF({ ...f, created_until: e.target.value })}
                className="sp-input" data-testid="filter-created-until" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("pipeline.drawer.lastMove")}</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input type="date" value={f.updated_since} onChange={(e) => setF({ ...f, updated_since: e.target.value })}
                className="sp-input" data-testid="filter-updated-since" />
              <input type="date" value={f.updated_until} onChange={(e) => setF({ ...f, updated_until: e.target.value })}
                className="sp-input" data-testid="filter-updated-until" />
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-6">
          <button onClick={reset} className="sp-btn-ghost text-sm" data-testid="filters-reset">{t("common.reset")}</button>
          <button onClick={apply} className="sp-btn-primary" data-testid="filters-apply">{t("pipeline.drawer.apply")}</button>
        </div>
      </div>
    </div>
  );
}

function NewOpportunityModal({ stages, onClose, onCreated }) {
  const { t } = useTranslation();
  const api = useApiClient();
  const [tab, setTab] = useState("existing"); // existing | new
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [stageKey, setStageKey] = useState(stages[0]?.key || "");
  const [busy, setBusy] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", phone: "", email: "" });

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients.slice(0, 30);
    return clients.filter((c) => `${c.name} ${c.phone || ""} ${c.email || ""}`.toLowerCase().includes(s)).slice(0, 30);
  }, [q, clients]);

  const useExisting = async () => {
    if (!selected || !stageKey) return;
    setBusy(true);
    try {
      await api.patch(`/pipeline/${selected.id}/stage`, { stage: stageKey });
      toast.success(t("pipeline.newOpp.added"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };
  const createNew = async () => {
    if (!newForm.name.trim()) return toast.error(t("pipeline.newOpp.nameRequired"));
    setBusy(true);
    try {
      const r = await api.post("/clients", {
        name: newForm.name.trim(),
        phone: newForm.phone || null,
        email: newForm.email || null,
        pipeline_stage: stageKey,
        tags: [],
      });
      toast.success(t("pipeline.newOpp.contactCreated", { name: r.data.name }));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="new-opp-modal">
      <div className="sp-card w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-black">{t("pipeline.newOpportunity")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-1 mb-4">
          <button onClick={() => setTab("existing")} data-testid="new-opp-tab-existing"
            className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg ${tab === "existing" ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)]"}`}>
            {t("pipeline.newOpp.existingClient")}
          </button>
          <button onClick={() => setTab("new")} data-testid="new-opp-tab-new"
            className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg ${tab === "new" ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)]"}`}>
            <UserPlus className="inline h-3 w-3 mr-1" /> {t("pipeline.newOpp.newContact")}
          </button>
        </div>

        <div className="mb-3">
          <label className="text-xs font-bold">{t("pipeline.newOpp.initialStage")}</label>
          <select value={stageKey} onChange={(e) => setStageKey(e.target.value)}
            className="sp-input mt-1" data-testid="new-opp-stage">
            {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {tab === "existing" ? (
          <div className="space-y-3">
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("pipeline.newOpp.searchClient")} className="sp-input"
              data-testid="new-opp-search" />
            <ul className="space-y-1 max-h-56 overflow-y-auto" data-testid="new-opp-results">
              {filtered.map((c) => (
                <li key={c.id}>
                  <button onClick={() => setSelected(c)}
                    data-testid={`new-opp-pick-${c.id}`}
                    className={`w-full text-left p-2 rounded-lg border ${selected?.id === c.id ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/5" : "border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]"}`}>
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-[11px] text-[color:var(--sp-text-muted)]">{c.phone || c.email || "—"}</div>
                  </button>
                </li>
              ))}
            </ul>
            <button onClick={useExisting} disabled={!selected || busy}
              data-testid="new-opp-use-existing-btn"
              className="sp-btn-primary w-full">
              {busy ? t("pipeline.newOpp.adding") : t("pipeline.newOpp.addOpportunity")}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input placeholder={t("pipeline.newOpp.namePlaceholder")} value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              className="sp-input" data-testid="new-opp-name" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder={t("pipeline.newOpp.phone")} value={newForm.phone}
                onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
                className="sp-input" data-testid="new-opp-phone" />
              <input placeholder={t("pipeline.newOpp.email")} type="email" value={newForm.email}
                onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                className="sp-input" data-testid="new-opp-email" />
            </div>
            <button onClick={createNew} disabled={busy} data-testid="new-opp-create-btn"
              className="sp-btn-primary w-full">
              {busy ? t("pipeline.newOpp.creating") : t("pipeline.newOpp.createAndAdd")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StagesSettingsModal({ stages: initial, onClose, onSaved }) {
  const { t } = useTranslation();
  const api = useApiClient();
  const [stages, setStages] = useState(() => initial.map((s) => ({ ...s })));
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  // Which stage row has its "advanced" (key) editor open
  const [advancedIdx, setAdvancedIdx] = useState(null);
  const [colorPickerIdx, setColorPickerIdx] = useState(null);

  const slugify = (label) => (label || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32) || `stage_${Math.random().toString(36).slice(2, 8)}`;

  const addStage = () => {
    const usedColors = new Set(stages.map((s) => s.color));
    const nextColor = STAGE_PALETTE.find((c) => !usedColors.has(c)) || STAGE_PALETTE[0];
    const label = t("pipeline.stages.newStage");
    setStages((s) => [...s, { key: slugify(label) + "_" + Math.random().toString(36).slice(2, 5), label, color: nextColor }]);
  };
  const removeStage = (i) => {
    if (stages.length <= 2) return toast.error(t("pipeline.stages.min2"));
    setStages((s) => s.filter((_, j) => j !== i));
  };
  const updateStage = (i, patch) => {
    setStages((s) => s.map((st, j) => j === i ? { ...st, ...patch } : st));
  };
  const onLabelChange = (i, newLabel) => {
    // Auto-sync key from label ONLY if the user never manually edited it
    // (heuristic: current key equals slugify of prior label).
    const st = stages[i];
    const priorAuto = slugify(st.label);
    const patch = { label: newLabel };
    if (st.key === priorAuto || st.key.startsWith("stage_")) {
      patch.key = slugify(newLabel);
    }
    updateStage(i, patch);
  };
  const onDragStart = (i) => setDragIdx(i);
  const onDrop = (i) => {
    if (dragIdx == null || dragIdx === i) return;
    setStages((s) => {
      const copy = [...s];
      const [moved] = copy.splice(dragIdx, 1);
      copy.splice(i, 0, moved);
      return copy;
    });
    setDragIdx(null);
  };


  // Close color picker when clicking outside its row
  useEffect(() => {
    if (colorPickerIdx == null) return;
    const onDoc = (e) => {
      const row = document.querySelector(`[data-testid="stage-row-${colorPickerIdx}"]`);
      if (row && !row.contains(e.target)) setColorPickerIdx(null);
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); };
  }, [colorPickerIdx]);

  const save = async () => {
    const keys = stages.map((s) => s.key);
    if (new Set(keys).size !== keys.length) return toast.error(t("pipeline.stages.duplicateKeys"));
    if (stages.some((s) => !s.label.trim())) return toast.error(t("pipeline.stages.labelRequired"));
    setSaving(true);
    try {
      await api.put("/pipeline/stages", { stages });
      toast.success(t("pipeline.stages.saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };
  const missingAuto = !stages.some((s) => s.key === "prenotato") || !stages.some((s) => s.key === "cliente");

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="pipeline-stages-modal">
      <div className="sp-card w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-black">{t("pipeline.stages.modalTitle")}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded flex items-center justify-center hover:bg-[color:var(--sp-subtle)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ═══════════════ Fasi della pipeline ═══════════════ */}
        <section data-testid="pipeline-stages-section">
          <h4 className="font-display text-lg font-black mb-1">{t("pipeline.stages.sectionTitle")}</h4>
          <p className="text-xs text-[color:var(--sp-text-muted)] mb-3">
            <Trans i18nKey="pipeline.stages.hint" components={{ 1: <b />, 3: <b /> }} />
          </p>
          {missingAuto && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="stages-auto-warning">
              <Trans i18nKey="pipeline.stages.warning" components={{ 0: <b />, 2: <code />, 4: <code /> }} />
            </div>
          )}
          <div className="space-y-2 mb-3" data-testid="stages-editor-list">
            {stages.map((s, i) => (
              <div key={i} draggable onDragStart={() => onDragStart(i)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                className="border border-[color:var(--sp-border)] rounded-xl p-2.5 bg-white hover:border-[color:var(--sp-brand)]/40 transition-colors"
                data-testid={`stage-row-${i}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-[color:var(--sp-text-muted)] cursor-move flex-shrink-0" />
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setColorPickerIdx(colorPickerIdx === i ? null : i)}
                      className="group h-7 w-7 rounded-full ring-1 ring-[color:var(--sp-border)] shadow-sm hover:ring-2 hover:ring-[color:var(--sp-brand)]/40 flex items-center justify-center transition-all"
                      style={{ background: s.color }}
                      title={t("pipeline.stages.changeColor")}
                      data-testid={`stage-swatch-${i}`}>
                      <Pencil className="h-3 w-3 text-white/0 group-hover:text-white/90 transition-colors" strokeWidth={2.5} />
                    </button>
                    {colorPickerIdx === i && (
                      <div
                        className="absolute z-30 mt-1.5 top-full left-0 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl p-3 w-[240px]"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`stage-color-picker-${i}`}>
                        <div className="grid grid-cols-5 gap-3">
                          {STAGE_PALETTE.map((c) => {
                            const selected = s.color === c;
                            return (
                              <button
                                key={c}
                                type="button"
                                onClick={() => { updateStage(i, { color: c }); setColorPickerIdx(null); }}
                                style={{ background: c }}
                                className={`relative h-8 w-8 rounded-full ring-1 ring-[color:var(--sp-border)] hover:ring-2 hover:ring-[color:var(--sp-brand)]/60 hover:scale-110 transition-all ${selected ? "ring-2 ring-offset-2 ring-[color:var(--sp-brand)]" : ""}`}
                                data-testid={`stage-color-${i}-${c}`}
                                title={c}>
                                {selected && <Check className="h-4 w-4 text-white absolute inset-0 m-auto" strokeWidth={3} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    value={s.label}
                    onChange={(e) => onLabelChange(i, e.target.value)}
                    placeholder={t("pipeline.stages.namePlaceholder")}
                    className="sp-input flex-1 !py-1.5"
                    data-testid={`stage-label-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => setAdvancedIdx(advancedIdx === i ? null : i)}
                    className={`text-xs font-bold px-2 py-1 rounded ${advancedIdx === i ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)]"}`}
                    data-testid={`stage-advanced-toggle-${i}`}>
                    ID
                  </button>
                  <button
                    type="button"
                    onClick={() => removeStage(i)}
                    className="text-red-600 hover:bg-red-50 p-1.5 rounded flex-shrink-0"
                    data-testid={`stage-remove-${i}`}
                    title={t("pipeline.stages.deleteStage")}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {advancedIdx === i && (
                  <div className="mt-2 pl-11" data-testid={`stage-advanced-${i}`}>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">
                      {t("pipeline.stages.technicalId")}
                    </label>
                    <input
                      value={s.key}
                      onChange={(e) => updateStage(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                      className="sp-input mt-1 text-xs font-mono"
                      data-testid={`stage-key-${i}`}
                    />
                    <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
                      <Trans i18nKey="pipeline.stages.technicalHint" components={{ 1: <code />, 3: <code /> }} />
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button onClick={addStage} data-testid="stage-add-btn"
            className="w-full py-2 rounded-lg border border-dashed border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] hover:text-[color:var(--sp-brand)] text-sm inline-flex items-center justify-center gap-1.5 transition-colors">
            <Plus className="h-4 w-4" /> {t("pipeline.stages.addStage")}
          </button>
        </section>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[color:var(--sp-border)]">
          <button onClick={onClose} className="sp-btn-ghost">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving} className="sp-btn-primary inline-flex items-center gap-2"
            data-testid="stages-save-btn">
            <Save className="h-4 w-4" /> {saving ? t("common.saving") : t("pipeline.stages.saveSettings")}
          </button>
        </div>
      </div>
    </div>
  );
}
